/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, inject, NgZone, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FirebaseFirestoreService } from '../../../services/firebase.firestore.service';
import { Router } from '@angular/router';
import { UtilityService } from '../../../services/utility.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { LocationService } from '../../../services/location.service';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-home-tab',
  standalone: true,
  imports: [CommonModule, ConfirmDialogModule],
  providers: [],
  templateUrl: './home-tab.component.html',
  styleUrls: ['./home-tab.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HomeTabComponent implements AfterViewInit, OnDestroy {
  firebaseFirestoreService = inject(FirebaseFirestoreService);
  router = inject(Router);
  userProfile = this.firebaseFirestoreService.getUserProfile();
  activeBatches = this.firebaseFirestoreService.getAcceptedBatches();
  activeBatchesCount = 0;
  expectedEarnings = 0;
  isAvailable = false;
  isUpdatingAvailability = false;
  ngZone = inject(NgZone);
  utilityService = inject(UtilityService);
  locationService = inject(LocationService);
  confirmationService = inject(ConfirmationService);
  constructor() {
    this.getTodaySummary();
    this.getUserProfile();
    this.getDevicePushToken();
  }

  ngAfterViewInit() {
    this.locationService.subscribeToLocationEvents();
    
    // Set up the dialog callback
    this.locationService.setDialogCallback((dialogType) => {
      if (dialogType === 'location-permission') {
        this.showLocationPermissionDialog();
      } else if (dialogType === 'open-settings') {
        this.showOpenSettingsDialog();
      } else if (dialogType === 'folsom-area') {
        this.showFolsomAreaDialog();
      } else if (dialogType === 'rancho-cordova-area') {
        this.showRanchoCordovaAreaDialog();
      } else if (dialogType === 'not-supported-city') {
        this.showNotSupportedCityDialog();
      } else if (dialogType === 'not-supported-area') {
        this.showNotSupportedAreaDialog();
      }
    });
  }

  ngOnDestroy() {
    // Clear the dialog callback to prevent memory leaks and duplicate dialogs
    this.locationService.clearDialogCallback();
  }

  getTodaySummary() {
    this.activeBatches.subscribe((batches) => {
      this.activeBatchesCount = batches.length;
      this.expectedEarnings = 0;
      batches.forEach((batch) => {
        this.expectedEarnings += batch.estimatedShopperPay;
      });
    });
  }

  async getUserProfile() {
    try {
      this.userProfile = this.firebaseFirestoreService.getUserProfile();
      this.isAvailable = this.userProfile?.isAvailable || false;
    } catch (error) {
      console.error('Error loading availability status:', error);
    }
  }

  async toggleAvailability() {
    if (this.isUpdatingAvailability) return;
    
    this.isUpdatingAvailability = true;
    try {
      const newStatus = !this.isAvailable;
      await this.firebaseFirestoreService.updateUserDocument({isAvailable: newStatus, currentLocation: this.utilityService.location || {latitude: 0, longitude: 0, timestamp: 0}});
      setTimeout(() => {
        this.firebaseFirestoreService.pushToDailyAvailabilityLogs(newStatus, this.utilityService.location || {latitude: 0, longitude: 0});
      }, 2000);
      this.isAvailable = newStatus;      
    } catch (error) {
      console.error('Error updating availability:', error);
    } finally {
      this.isUpdatingAvailability = false;
    }
  }

  viewAvailableBatches() {
    this.router.navigate([], { queryParams: { tab: 1 , subTab: 0}, queryParamsHandling: 'merge' });
  }

  startShopping() {
    // add query param to same url
    this.router.navigate([], { queryParams: { tab: 1 , subTab: 1}, queryParamsHandling: 'merge' });
  }

  getDevicePushToken() {
    (window as any).ReactNativeWebView?.postMessage(JSON.stringify({ action: "get-device-push-token", data: null }));
  }

  showLocationPermissionDialog() {
    this.confirmationService.confirm({
      message: 'We need your location to see if we are operating in your area.',
      header: 'Allow Location Access',
      closeOnEscape: false,
      closable: false,
      dismissableMask: false,
      acceptLabel: 'Continue',
      acceptVisible: true,
      rejectVisible: false,
      position: 'bottom',
      accept: () => {
        this.locationService.getUserLocation();
      },
      reject: () => {
        return;
      }
    });
  }

  showOpenSettingsDialog() {
    this.confirmationService.confirm({
      message: 'Allow AnyStore to access your location. We need your location to see if we are operating in your area.',
      header: 'Allow Location Access',
      acceptLabel: 'Open Settings',
      acceptVisible: true,
      rejectLabel: 'No, Thanks',
      rejectVisible: true,
      position: 'bottom',
      accept: () => {
        if(!this.utilityService.locationPermissionStatus?.granted) {
          (window as any).ReactNativeWebView?.postMessage(JSON.stringify({action: "open-settings", data: null}));
        } else {
          this.locationService.getUserLocation();
        }
      },
      reject: () => {
        return;
      }
    });
  }

  showFolsomAreaDialog() {
    this.confirmationService.confirm({
      message: "Awesome news — we're up and running in Folsom city. You can now accept batches you like and start earning 😊",
      header: 'We are in your area!',
      acceptLabel: 'Got It!',
      acceptVisible: true,
      rejectVisible: false,
      closeOnEscape: false,
      closable: false,
      dismissableMask: false,
      position: 'bottom',
      accept: () => {
        return;
      },
      reject: () => {
        return;
      }
    });
  }

  showRanchoCordovaAreaDialog() {
    this.confirmationService.confirm({
      message: "Awesome news — we're up and running in Rancho Cordova city. You can now accept batches you like and start earning 😊",
      header: 'We are in your area!',
      acceptLabel: 'Got It!',
      acceptVisible: true,
      rejectVisible: false,
      closeOnEscape: false,
      closable: false,
      dismissableMask: false,
      position: 'bottom',
      accept: () => {
        return;
      },
      reject: () => {
        return;
      }
    });
  }

  showNotSupportedCityDialog() {
    this.confirmationService.confirm({
      message: 'Sorry! We are not yet operating in your city.',
      header: 'We are not in your area!',
      acceptLabel: 'Vote for my city',
      acceptVisible: false,
      rejectLabel: 'No, Thanks',
      rejectVisible: false,
      position: 'bottom',
      accept: () => {
        this.firebaseFirestoreService.addLocationRequest(this.firebaseFirestoreService.getUserProfile()?.uid as string, this.utilityService.location || { latitude: 0, longitude: 0 }, true);
        return;
      },
      reject: () => {
        this.firebaseFirestoreService.addLocationRequest(this.firebaseFirestoreService.getUserProfile()?.uid as string, this.utilityService.location || { latitude: 0, longitude: 0 }, false);
        return;
      }
    });
  }

  showNotSupportedAreaDialog() {
    this.confirmationService.confirm({
      message: 'Appreciate your interest. We are working hard to start operations in your area and provide services to you soon.',
      header: 'We are not in your area!',
      acceptLabel: 'Got It!',
      acceptVisible: true,
      rejectVisible: false,
      closeOnEscape: false,
      closable: false,
      dismissableMask: false,
      position: 'bottom',
      accept: () => {
        return;
      },
      reject: () => {
        return;
      }
    });
  }
} 